import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Modal } from '@rsos/base-components';
import {
  ModalHeader,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import { updateUserAttributes } from '@rsos/sinatra';
import { captureException } from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import { ctmEnableErrorMsg } from '../constants';
import { ButtonContainer, ModalDescription, ModalTitle } from './Modal.styles';

/**
 * @typedef {Object} InitiationModalProps
 * @property {() => void} [closeInitiationModal] - Callback function to close the modal.
 * @property {boolean} [isOpened] - Is the modal open?
 * @property {() => void} [handleTurnOnClick] - Callback function to enable call taker mode.
 */

/**
 * Call taker mode - initial modal.
 * @param {InitiationModalProps} props
 */
const InitiationModal = ({
  closeInitiationModal = () => {},
  isOpened = false,
  handleTurnOnClick = () => {},
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.sinatra);
  const applicationName = user.currentRole.application;

  /**
   * First time logging in with CallTakerMode permission enabled
   * but does not have call_taker_mode_settings in sinatra
   * preset call_taker_mode_settings in sinatra will prevent future prompt of this modal
   */
  useEffect(() => {
    const data = {
      call_taker_mode_settings: {
        auto_switch_to_next_call: false,
        is_enabled: false,
        remember_workstation_position: false,
        workstation_position: '',
      },
    };
    try {
      dispatch(updateUserAttributes(applicationName, data));
    } catch (err) {
      notifyError(ctmEnableErrorMsg);
      captureException(err);
    }
  }, [applicationName, dispatch]);

  const handleTurnOnCTM = () => {
    handleTurnOnClick();
    closeInitiationModal();
  };

  return (
    <Modal
      id="call-taker-mode-initial-modal"
      isOpened={isOpened}
      onClose={closeInitiationModal}
      padding="45px 45px 35px"
      width={350}
    >
      <ModalHeader onClose={closeInitiationModal}>
        <Box marginBottom="19px">
          <ModalTitle data-name="modal-title">
            New Feature: Call-Taker Mode
          </ModalTitle>
          <ModalDescription data-name="modal-description">
            Call-taker mode introduces a way for telecommunicators to better
            access data. All calls that you answer from your seat position will
            automatically open the related event in the RapidSOS portal.
          </ModalDescription>
        </Box>
      </ModalHeader>
      <ModalFooter>
        <ButtonContainer>
          <Button
            btnType="primary"
            dataName="call-taker-mode-enable-button"
            noMargin
            onClick={handleTurnOnCTM}
          >
            Turn on
          </Button>
          <Button
            btnType="tertiary"
            dataName="call-taker-mode-cancel-button"
            noMargin
            onClick={closeInitiationModal}
          >
            Cancel
          </Button>
        </ButtonContainer>
      </ModalFooter>
    </Modal>
  );
};

export default InitiationModal;
